import {
  Dialog,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  IconButton,
  Typography,
  Box,
  makeStyles,
} from "@material-ui/core";
import { Add, Close } from "@material-ui/icons";
import React, { useEffect, useState } from "react";
import { useNotify, useRefresh } from "react-admin";
import { useAppDataProvider } from "../../../src/dataprovider";

const useStyles = makeStyles(theme => ({
  dialogTitle: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    gap: 8,
    padding: "16px 24px",
  },
  titleIcon: {
    color: "#6366F1",
    fontSize: 28,
  },
  titleText: {
    fontWeight: 600,
    fontSize: 18,
  },
  closeButton: {
    marginLeft: "auto",
  },
  dialogContent: {
    padding: "16px 24px",
    minWidth: 400,
  },
  warningBox: {
    backgroundColor: "#FEF3C7",
    border: "1px solid #F59E0B",
    borderRadius: 4,
    padding: 12,
    marginBottom: 16,
  },
  warningText: {
    color: "#92400E",
    fontSize: 14,
  },
  label: {
    fontSize: 14,
    color: "#374151",
    marginBottom: 8,
    display: "block",
  },
  required: {
    color: "#EF4444",
    marginLeft: 4,
  },
  textField: {
    "& .MuiOutlinedInput-root": {
      "&.Mui-error": {
        "& fieldset": {
          borderColor: "#EF4444",
        },
      },
    },
  },
  dialogActions: {
    padding: "16px 24px",
    borderTop: "2px solid #6366F1",
  },
  cancelButton: {
    color: "#374151",
    borderColor: "#D1D5DB",
    "&:hover": {
      borderColor: "#9CA3AF",
    },
  },
  saveButton: {
    backgroundColor: theme.palette.primary.main,
    color: "#fff",
    "&:hover": {
      backgroundColor: theme.palette.primary.dark,
    },
  },
}));

interface AdvancedXoPopupProps {
  open: boolean;
  handleClose: (value: boolean) => void;
  record: any;
  callback?: () => void;
}

export default function AdvancedXoPopup(props: AdvancedXoPopupProps) {
  const { open, handleClose, record, callback } = props;
  const [advancedXo, setAdvancedXo] = useState("");
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);
  const classes = useStyles();
  const dataProvider = useAppDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();

  useEffect(() => {
    if (open) {
      setAdvancedXo(record?.advancedXo || "");
      setError(false);
    }
  }, [open, record]);

  const handleSave = async () => {
    if (!advancedXo.trim()) {
      setError(true);
      return;
    }
    setError(false);
    setLoading(true);

    try {
      const response = await dataProvider.getOne("pbhrequestorders/", { id: record.id });
      const updatedRecord = { ...response.data, advancedXo: advancedXo.trim() };
      await dataProvider.updatePbhRequestOrder(updatedRecord);
      notify("Record updated", "success", false, true);
      callback?.();
      refresh();
      handleClose(false);
    } catch (err) {
      notify(`${err}`, "error", false, true);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setAdvancedXo("");
    setError(false);
    handleClose(false);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setAdvancedXo(e.target.value);
    if (error && e.target.value.trim()) {
      setError(false);
    }
  };

  const existingAdvancedXo = record?.advancedXo;

  return (
    <Dialog open={open} onClose={handleCancel} maxWidth="sm">
      <Box className={classes.dialogTitle}>
        <Add className={classes.titleIcon} />
        <Typography className={classes.titleText}>Add Advanced XO</Typography>
        <IconButton className={classes.closeButton} onClick={handleCancel} size="small">
          <Close />
        </IconButton>
      </Box>

      <DialogContent className={classes.dialogContent}>
        {existingAdvancedXo && (
          <Box className={classes.warningBox}>
            <Typography className={classes.warningText}>
              <strong>Warning!</strong> An Advanced XO <strong>{existingAdvancedXo}</strong> exists for this request.
              Please use it for submission.
            </Typography>
          </Box>
        )}

        <label className={classes.label}>
          Advanced XO
          <span className={classes.required}>(Required)</span>
        </label>
        <TextField
          className={classes.textField}
          placeholder="Ex: XO-2024-00123"
          variant="outlined"
          fullWidth
          size="small"
          value={advancedXo}
          onChange={handleChange}
          error={error}
          helperText={error ? "This field is required" : ""}
        />
      </DialogContent>

      <DialogActions className={classes.dialogActions}>
        <Button className={classes.cancelButton} variant="outlined" onClick={handleCancel} disabled={loading}>
          Cancel
        </Button>
        <Button className={classes.saveButton} variant="contained" onClick={handleSave} disabled={loading}>
          {loading ? "Saving..." : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
